/**
 * @file qidian-i18n locale-reader 标准模板（v1.5.0）
 *
 * 来源：qidian-i18n-spec / templates/locale-reader/locale-reader.js
 *
 * 设计原则（不可违反）：
 *   1. 纯函数 + 无依赖（不依赖 vue / vue-i18n / 任何运行时）
 *   2. 可被 tree-shake（每个函数独立 export）
 *   3. SSR 安全（所有 window/document/localStorage 调用必须先判断）
 *   4. 向后兼容（升级新版本不应破坏现有调用）
 *
 * 禁止事项：
 *   ❌ 禁止在各项目本地修改本文件
 *   ❌ 禁止 fork 出私有版本
 *   ❌ 所有改动必须走 qidian-i18n-spec 仓库 PR
 *
 * 用法见同目录 README.md。
 *
 * @typedef {'zh-CN'|'en-US'} Lang
 */

/* ============================================================================
 * 类型与常量
 * ========================================================================== */

/** 主存储 key（基于 qidian-vea 现有约定） */
export const PRIMARY_LOCALE_KEY = 'qidianLocale';

/** 历史 Cookie key（兼容 qidian-workbench 老入口） */
export const LEGACY_COOKIE_LANG = 'lang';

/** ti18n SDK 兼容 key（gpt-demo / qidian-cs-static） */
export const TI18N_LOCALE_KEY = 'ti18nLng';

/**
 * 跨子域共享 cookie key（v1.5.0 新增）。
 *
 * 与 `localStorage['qidianLocale']` 同名以保持语义一致；值为标准 Lang
 * （`zh-CN` / `en-US`），不是 legacy 小写。
 *
 * **存在意义**：localStorage 与 host-only cookie 都按 origin 隔离，
 * 在 `A.qidian.qq.com` / `B.qidian.qq.com` 等子域之间不共享。这条
 * cookie 写到父域 `.qidian.qq.com`，确保跨子域的语言一致性。
 *
 * 优先级链中的位置见 `getQidianLocale`。
 */
export const SHARED_COOKIE_LOCALE = 'qidianLocale';

/**
 * 跨子域共享 cookie 的 domain。
 *
 * 直接写死为企点公司域 `.qidian.qq.com`：
 *   - 该模板仅服务企点 15 个项目，部署域名固定为 `*.qidian.qq.com`；
 *   - 浏览器对"无法解析为父域"的 domain（如 `localhost` / IP / 私有化）会
 *     拒绝写入该 cookie，但不会抛错，写入端会自动降级为只写本域；
 *   - 因此本地开发 / 私有化 / 联调环境无需特别处理：
 *     `setQidianLocale` 仍能成功写入 `localStorage` / `window.lang` / 兼容
 *     cookie，只是少了"跨子域共享"这层增益。
 */
export const SHARED_COOKIE_DOMAIN = '.qidian.qq.com';

/**
 * 共享 cookie 持久化时长（秒）。默认 1 年。
 * 不再依赖会话 cookie，关闭浏览器后语言选择仍保留。
 */
export const SHARED_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

/** 全局事件名 */
export const LOCALE_EVENT = 'qidian-locale-change';

/** 默认语言（兜底） */
export const DEFAULT_LOCALE = 'zh-CN';

/* ============================================================================
 * 环境判断
 * ========================================================================== */

function isBrowser() {
    return typeof window !== 'undefined';
}

function hasLocalStorage() {
    try {
        return isBrowser() && typeof window.localStorage !== 'undefined';
    } catch (e) {
        return false;
    }
}

function hasDocument() {
    return typeof document !== 'undefined';
}

function isKnownLocaleInput(locale) {
    const value = String(locale || '').replace('_', '-').toLowerCase();
    return value === 'zh' || value === 'zh-cn' || value === 'zh-hans'
        || value === 'en' || value === 'en-us';
}

/* ============================================================================
 * 语言码标准化与互转
 * ========================================================================== */

/**
 * 把任意语言输入归一化为标准 Lang。
 *
 * 规则：
 *   - 'zh' / 'zh-cn' / 'zh_CN' / 'zh-hans' / 'zh-Hans' → 'zh-CN'
 *   - 'en' / 'en-us' / 'en_US'                          → 'en-US'
 *   - 其他                                               → 'zh-CN'（兜底）
 *
 * @param {string|null} [locale]
 * @returns {Lang}
 */
export function normalizeLocale(locale) {
    const value = String(locale || '').replace('_', '-').toLowerCase();
    if (value === 'zh' || value === 'zh-cn' || value === 'zh-hans') return 'zh-CN';
    if (value === 'en' || value === 'en-us') return 'en-US';
    return DEFAULT_LOCALE;
}

/**
 * 转换为 legacy lang 值（小写连字符）。
 * 用于写入 Cookie['lang'] / URL '?lang='。
 */
export function toLegacyLang(locale) {
    return normalizeLocale(locale) === 'en-US' ? 'en-us' : 'zh-cn';
}

/**
 * 转换为短码。
 * 用于 ti18n SDK / gpt-demo 兼容（zh / en）。
 */
export function toShortLang(locale) {
    return normalizeLocale(locale) === 'en-US' ? 'en' : 'zh';
}

/* ============================================================================
 * Cookie / URL 读取工具
 * ========================================================================== */

/**
 * 读取 Cookie 值。
 *
 * 注意：浏览器 `document.cookie` 不暴露 Domain 属性，因此当本域同时存在
 * 一条 host-only `name` 与一条 `Domain=.qidian.qq.com` 的 `name` 时，
 * 字符串里会出现两条同名记录，匹配顺序在不同浏览器实现中不稳定。
 * 这是 v1.5.0 引入独立 `SHARED_COOKIE_LOCALE` 的核心理由：
 * 跨子域共享走独立 key，避免与 legacy `lang` 同名冲突。
 */
export function getCookie(name) {
    if (typeof document === 'undefined') return '';
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
}

/**
 * 读取 URL 参数（含 hash 后的 query）。
 *
 * 兼容场景：
 *   - 普通 URL：?lang=en-US
 *   - hash 路由：#/page?lang=en-US
 */
export function getUrlParam(name) {
    if (!isBrowser()) return '';
    const searchValue = new URLSearchParams(window.location.search).get(name);
    if (searchValue) return searchValue;
    const hashQuery = window.location.hash.split('?')[1] || '';
    return new URLSearchParams(hashQuery).get(name) || '';
}

/* ============================================================================
 * 跨子域共享 cookie 写入（v1.5.0 §3.3）
 * ========================================================================== */

/**
 * 把 locale 写入父域共享 cookie（`Domain=.qidian.qq.com`）。
 *
 * 浏览器会根据当前 host 自行判断 domain 是否可接受：
 *   - `*.qidian.qq.com` 子域：写入成功，所有兄弟子域可共享；
 *   - `localhost` / IP / 其他主域：浏览器静默拒绝 set-cookie，无副作用。
 *
 * 失败时不抛错，由调用方决定是否降级（当前 `setQidianLocale` 不依赖
 * 这条 cookie 的写入是否成功——`localStorage` / `window.lang` / 兼容
 * cookie 仍会照常写入）。
 *
 * @param {Lang} locale 标准 Lang，必须已经 `normalizeLocale` 过。
 */
function writeSharedCookie(locale) {
    if (!hasDocument()) return;
    const value = encodeURIComponent(locale);
    document.cookie = SHARED_COOKIE_LOCALE + '=' + value
        + '; path=/'
        + '; domain=' + SHARED_COOKIE_DOMAIN
        + '; max-age=' + SHARED_COOKIE_MAX_AGE
        + '; SameSite=Lax';
}

/**
 * 读取跨子域共享 cookie 中的 locale；缺失或非法值返回空串。
 *
 * 不做 normalize，由 `getQidianLocale` 在最终聚合时统一 normalize；
 * 这样空串能进入下一档 fallback，而不会被错误地兜底成 `zh-CN`。
 */
export function getSharedLocaleCookie() {
    const value = getCookie(SHARED_COOKIE_LOCALE);
    return isKnownLocaleInput(value) ? normalizeLocale(value) : '';
}

/* ============================================================================
 * 核心：Locale 优先级链（最终方案 v1.5 §3.2）
 * ========================================================================== */

/**
 * 按企点 i18n 标准优先级链解析当前语言。
 *
 * 优先级顺序（v1.5.0 调整）：
 *   1. 显式 explicitLocale 参数（最高）
 *   2. URL ?locale=
 *   3. URL ?lang=
 *   4. URL ?language=
 *   5. window.__QIDIAN_LOCALE__
 *   6. window.lang
 *   7. **Cookie['qidianLocale']（跨子域共享，v1.5.0 新增）**
 *   8. localStorage['qidianLocale']
 *   9. Cookie['lang']
 *   10. Cookie['language']
 *   11. Cookie['intl_language']
 *   12. localStorage['ti18nLng'] / Cookie['ti18nLng']
 *   13. navigator.language
 *   14. 'zh-CN'（兜底）
 *
 * 关键点：跨子域共享 cookie 排在 `localStorage` **之前**。
 * 这样在 `B.qidian.qq.com` 第一次拿到 A 设置的共享 cookie 时，
 * 即便 B 自己的 localStorage 仍是旧值，也会优先走共享 cookie 的新值；
 * 之后 `setQidianLocale` 再把 B 的 localStorage 同步覆盖。
 *
 * @param {string} [explicitLocale]
 * @returns {Lang}
 */
export function getQidianLocale(explicitLocale) {
    if (!isBrowser()) return normalizeLocale(explicitLocale);

    const globalLocale = window.__QIDIAN_LOCALE__ || '';
    const windowLang = window.lang || '';
    const sharedCookieLocale = getSharedLocaleCookie();
    const storageLocale = hasLocalStorage() ? (window.localStorage.getItem(PRIMARY_LOCALE_KEY) || '') : '';
    const ti18nLocale = hasLocalStorage() ? (window.localStorage.getItem(TI18N_LOCALE_KEY) || '') : '';
    const browserLocale = (window.navigator && window.navigator.language) || '';

    return normalizeLocale(
        explicitLocale
        || getUrlParam('locale')
        || getUrlParam('lang')
        || getUrlParam('language')
        || globalLocale
        || windowLang
        || sharedCookieLocale
        || storageLocale
        || getCookie(LEGACY_COOKIE_LANG)
        || getCookie('language')
        || getCookie('intl_language')
        || ti18nLocale
        || getCookie(TI18N_LOCALE_KEY)
        || browserLocale
        || DEFAULT_LOCALE
    );
}

/* ============================================================================
 * 核心：Locale 写入（最终方案 v1.5 §3.3）
 * ========================================================================== */

/** 内部状态：当前语言 */
let currentLocale = isBrowser() ? getQidianLocale() : DEFAULT_LOCALE;

/** 订阅者集合 */
const listeners = new Set();

/**
 * 获取当前语言（不重新解析，仅读内存）。
 */
export function getCurrentLocale() {
    return currentLocale;
}

/**
 * 设置当前语言并同步到所有约定的存储位置 + 触发事件。
 *
 * 副作用（v1.5.0 §3.3）：
 *   1. window.__QIDIAN_LOCALE__ = locale
 *   2. window.lang = locale
 *   3. localStorage['qidianLocale'] = locale
 *   4. Cookie['lang'] = legacyLang （host-only，兼容老入口）
 *   5. **Cookie['qidianLocale'] = locale（Domain=.qidian.qq.com，跨子域共享）**
 *   6. dispatchEvent('qidian-locale-change', { locale, legacyLang })
 *   7. 调用所有 onLocaleChange 订阅者
 *
 * 设计要点（与 v1.4.x 的差异）：
 *   - 跨子域共享走**独立 cookie key** `qidianLocale`，**不复用** `lang`；
 *     避免本域 host-only `lang` 与父域 `lang` 同名冲突，浏览器返回顺序
 *     在不同实现中不可靠。
 *   - 共享 cookie 显式带 `max-age` 1 年，不再是 session cookie。
 *   - 非 `*.qidian.qq.com` 域名（本地 / 私有化）浏览器会拒绝写入父域
 *     cookie，此处不会抛错，自然降级为"仅当前域生效"。
 *
 * @returns {Lang} 实际生效的标准语言码
 */
export function setQidianLocale(locale) {
    const nextLocale = normalizeLocale(locale);
    const legacyLang = toLegacyLang(nextLocale);
    currentLocale = nextLocale;

    if (isBrowser()) {
        window.__QIDIAN_LOCALE__ = nextLocale;
        window.lang = nextLocale;
        if (hasLocalStorage()) {
            window.localStorage.setItem(PRIMARY_LOCALE_KEY, nextLocale);
        }
        if (hasDocument()) {
            // 兼容老入口：host-only `lang` cookie，值为 legacy 小写
            document.cookie = LEGACY_COOKIE_LANG + '=' + encodeURIComponent(legacyLang) + '; path=/';
            // 跨子域共享：父域 `qidianLocale` cookie，值为标准 Lang
            writeSharedCookie(nextLocale);
        }
        window.dispatchEvent(new CustomEvent(LOCALE_EVENT, {
            detail: { locale: nextLocale, legacyLang },
        }));
    }

    listeners.forEach((fn) => fn(nextLocale));
    return nextLocale;
}

/* ============================================================================
 * 订阅机制（供组件库 / 业务层响应语言变化）
 * ========================================================================== */

/**
 * 订阅语言变化。
 *
 * @param {(locale: Lang) => void} handler
 * @returns {() => void} 取消订阅函数
 */
export function onLocaleChange(handler) {
    listeners.add(handler);
    return () => {
        listeners.delete(handler);
    };
}

/**
 * 别名：与 onLocaleChange 等价，保留供历史调用方使用。
 * 不要在新代码中使用；请用 onLocaleChange。
 */
export const onQidianLocaleChange = onLocaleChange;

/* ============================================================================
 * 全局事件监听（自动接收来自其他模块/微前端的切换）
 * ========================================================================== */

if (isBrowser()) {
    window.addEventListener(LOCALE_EVENT, (event) => {
        const detail = event && event.detail;
        const locale = normalizeLocale(detail && detail.locale);
        if (locale !== currentLocale) {
            currentLocale = locale;
            listeners.forEach((fn) => fn(locale));
        }
    });
}

/* ============================================================================
 * 测试钩子（仅供单测使用，业务代码不要调用）
 * ========================================================================== */

/**
 * @internal 重置内部状态。仅供单测使用。
 */
export function __resetForTest() {
    currentLocale = DEFAULT_LOCALE;
    listeners.clear();
}
